import { type FormEvent, useCallback, useState } from 'react'

import i18n from './i18n'
import css from './index.module.css'

import { Section } from '#/local/component/Section'

import { Validate } from '#/local/service/Validation'

import { sendReport } from '#/local/repository'
import type { FetchResponse } from '#/local/type'

import { Checkbox } from '#/global/component/Form/Checkbox'
import { DatePicker } from '#/global/component/Form/DatePicker'
import { Form as FormWrap } from '#/global/component/Form/Form'
import { Input } from '#/global/component/Form/Input'
import { PhoneNumber } from '#/global/component/Form/PhoneNumber'
import { type SelectList, Select } from '#/global/component/Form/Select'
import { Textarea } from '#/global/component/Form/Textarea'
import type { InputState } from '#/global/component/Form/types'
import { Upload } from '#/global/component/Form/Upload'

import { Config } from '#/global/helper/Config'
import { MessageFlash } from '#/global/helper/MessageFlash'

export type FormFields = keyof Omit<FormState, 'files'>
export interface FormState {
    borrower?: string
    contactPerson?: string
    damageEvent?: DamageEvent
    damageEventDescription?: string
    dateDamage?: string
    machineId?: string
    phoneNumber?: string
}

type DamageEvent = 'Work' | 'ThirdPerson' | 'Vandalism' | 'NaturalElement' | 'Alienation' | 'Other'

interface StatusState {
    borrower?: InputState
    contactPerson?: InputState
    damageEvent?: InputState
    damageEventDescription?: InputState
    dateDamage?: InputState
    files?: InputState
    gdpr?: InputState
    machineId?: InputState
    phoneNumber?: InputState
}

const t = i18n[Config.getLang()]

export const App = () => {
    const [formState, setFormState] = useState<FormState>({
        phoneNumber: '+420 ',
    })

    const [files, setFiles] = useState<File[]>([])

    const [inputStatus, setInputStatus] = useState<StatusState>({})

    const [gdpr, setGdpr] = useState(false)
    const [isSend, setIsSend] = useState(false)

    const validate = useCallback((inputName: FormFields, inputValue: string | undefined) => {
        if (!inputValue) {
            setInputStatus(is => ({ ...is, [inputName]: undefined }))
            return
        }

        if (inputName === 'machineId') {
            setInputStatus(is => ({ ...is, machineId: { level: 'success' } }))
            return
        }

        if (inputName === 'phoneNumber') {
            if (inputValue === '+420') {
                return
            }
            inputValue = transformPhoneNumberForApi(inputValue)
        }

        Validate.checkRentalDamage(inputName, inputValue)
            .then((result) => {
                if (result.valid) {
                    setInputStatus(is => ({ ...is, [inputName]: { level: 'success' } }))
                    return
                }
                const error = result.errors.pop()
                if (error) {
                    console.error(error)
                    setInputStatus(is => ({ ...is, [inputName]: { level: 'error', message: error.message } }))
                }
            })
            .catch((error) => {
                console.error(error)
            })
    }, [])

    const onInputChange = useCallback((inputName: FormFields, inputValue: string) => {
        setFormState(fs => ({ ...fs, [inputName]: inputValue }))
    }, [])

    const onInputBlur = useCallback((inputName: FormFields) => {
        validate(inputName, formState[inputName])
    }, [formState, validate])

    const onGdprChange = useCallback((value: boolean) => {
        setGdpr(value)
        // při změně gdpr se zruší chyba v gdpr
        setInputStatus(is => ({ ...is, gdpr: undefined }))
    }, [])

    const onDateChange = useCallback((value: Date | null) => {
        if (!value) {
            return
        }
        setFormState(fs => ({ ...fs, dateDamage: value.toISOString() }))
        validate('dateDamage', value.toISOString())
    }, [validate])

    const onFileChange = useCallback((value: DataTransfer) => {
        setFiles([...value.files])
    }, [])

    const createDt = useCallback(() => {
        const dt = new DataTransfer()
        for (const file of files) {
            dt.items.add(file)
        }
        return dt
    }, [files])

    const handleSend = useCallback((event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setIsSend(true)
        if (!gdpr) {
            setInputStatus(is => ({ ...is, gdpr: { level: 'error', message: t.gdprError } }))
            setIsSend(false)
            return
        }

        const formStateCopy = { ...formState, files }
        formStateCopy.phoneNumber = transformPhoneNumberForApi(formStateCopy.phoneNumber!)

        const parseError = (errors: FetchResponse[]) => {
            const state: StatusState = {}
            for (const error of errors) {
                state[getInputIdByErrorPath(error.path)] = { level: 'error', message: error.message }
            }
            setInputStatus(is => ({ ...is, ...state }))
        }

        sendReport(formStateCopy)
            .then((result) => {
                if (!result.status) {
                    parseError(result.errors)
                    setIsSend(false)
                }
                setIsSend(false)
                setFormState({})
                setGdpr(false)
                setInputStatus({})
                setFiles([])

                MessageFlash.add({ type: 'success', message: t.success })
            })
            .catch((error) => {
                console.error(error)
            })
            .finally(() => {
                setIsSend(false)
            })
    }, [formState, gdpr, files])

    return (
        <FormWrap onSubmit={handleSend}>
            <div className={css.grid}>
                <Section full label={t.damageDate.title}>
                    <DatePicker
                        required
                        withTime
                        disabled={isSend}
                        gap="2rem"
                        onChange={onDateChange}
                        state={inputStatus.dateDamage}
                        timeLabel={t.damageDate.time}
                        value={formState.dateDamage ? new Date(formState.dateDamage) : undefined}>
                        {t.damageDate.date}
                    </DatePicker>
                </Section>
                <Section label={t.borrower.title}>
                    <div className={css.inline}>
                        <Input
                            required
                            disabled={isSend}
                            name="borrower"
                            onBlur={() => onInputBlur('borrower')}
                            onChange={v => onInputChange('borrower', v)}
                            size="medium"
                            state={inputStatus.borrower}
                            value={formState.borrower ?? ''}>
                            {t.borrower.label}
                        </Input>
                    </div>
                </Section>
                <Section wideEnd label={t.contactPerson.title}>
                    <div className={css.itemGroup}>
                        <div>
                            <Input
                                required
                                disabled={isSend}
                                name="contactPerson"
                                onBlur={() => onInputBlur('contactPerson')}
                                onChange={v => onInputChange('contactPerson', v)}
                                size="medium"
                                state={inputStatus.contactPerson}
                                value={formState.contactPerson ?? ''}>
                                {t.contactPerson.name}
                            </Input>
                        </div>
                        <div>
                            <PhoneNumber
                                required
                                disabled={isSend}
                                name="phoneNumber"
                                onBlur={() => onInputBlur('phoneNumber')}
                                onChange={v => onInputChange('phoneNumber', v)}
                                size="medium"
                                state={inputStatus.phoneNumber}
                                value={formState.phoneNumber ?? ''}>
                                {t.contactPerson.phone}
                            </PhoneNumber>
                        </div>
                    </div>
                </Section>
                <div className={css.wideStart}>
                    <div className={css.itemGroup}>
                        <div>
                            <Section label={t.machineId.title}>
                                <Input
                                    required
                                    disabled={isSend}
                                    name="machineId"
                                    onBlur={() => onInputBlur('machineId')}
                                    onChange={v => onInputChange('machineId', v)}
                                    size="medium"
                                    state={inputStatus.machineId}
                                    value={formState.machineId ?? ''}>
                                    {t.machineId.label}
                                </Input>
                            </Section>
                        </div>
                        <div>
                            <Section label={t.damageEvent.title}>
                                <Select
                                    required
                                    className={css.damageEvent}
                                    disabled={isSend}
                                    list={damageEventList}
                                    name="damageEvent"
                                    onBlur={() => onInputBlur('damageEvent')}
                                    onChange={v => onInputChange('damageEvent', v)}
                                    size="medium"
                                    state={inputStatus.damageEvent}
                                    value={formState.damageEvent ?? ''}>
                                    {t.damageEvent.label}
                                </Select>
                            </Section>
                        </div>
                    </div>
                    {formState.damageEvent === 'Other' && (
                        <div className={css.textAreaWrap}>
                            <Textarea
                                required
                                disabled={isSend}
                                name="description"
                                onBlur={() => onInputBlur('damageEventDescription')}
                                onChange={v => onInputChange('damageEventDescription', v)}
                                rows={5}
                                state={inputStatus.damageEventDescription}
                                value={formState.damageEventDescription ?? ''}>
                                {t.description.label}
                            </Textarea>
                        </div>
                    )}
                </div>
                <Section label={t.file.label}>
                    <Upload
                        multiple
                        accept=".png, .jpeg, .gif, .webp, .jpg"
                        dataTransfer={createDt()}
                        disabled={isSend}
                        maxTotalSize={15 * 1024 * 1024}
                        onChange={onFileChange}
                        size="medium"
                        state={inputStatus.files}>
                        {t.file.title}
                    </Upload>
                </Section>
            </div>
            <hr />
            <Checkbox
                checked={gdpr}
                className={css.gdpr}
                disabled={isSend}
                onChange={onGdprChange}
                state={inputStatus.gdpr}>
                {t.gdpr}
            </Checkbox>
            <div className={css.button}>
                <button
                    className={`dek-button-green ${isSend ? css.loading : ''}`.trim()}
                    disabled={isSend}
                    role="button"
                    type="submit">
                    {t.button}
                </button>
            </div>
        </FormWrap>
    )
}

const damageEventList: SelectList<'' | DamageEvent> = [
    {
        value: '',
        label: t.damageEvent.options.prompt,
    },
    {
        value: 'Work',
        label: t.damageEvent.options.Work,
    },
    {
        value: 'ThirdPerson',
        label: t.damageEvent.options.ThirdPerson,
    },
    {
        value: 'Vandalism',
        label: t.damageEvent.options.Vandalism,
    },
    {
        value: 'NaturalElement',
        label: t.damageEvent.options.NaturalElement,
    },
    {
        value: 'Alienation',
        label: t.damageEvent.options.Alienation,
    },
    {
        value: 'Other',
        label: t.damageEvent.options.Other,
    },
]

const getInputIdByErrorPath = (path: `$.${FormFields}`) => path.replace('$.', '') as FormFields

const transformPhoneNumberForApi = (value: string) => value.replaceAll(' ', '').replace(/(\+42[01])/, '$1 ')
